import { ReplyType } from "@sendbird/chat/message";
import { GroupChannelProvider } from "@sendbird/uikit-react/GroupChannel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useCallback, useMemo, useState } from "react";
import { Escalation } from "@/components/conversations/escalation";
import { CallConfirmationModal } from "@/components/group-call/call-confirmation-modal";
import { ScheduleSessionDialog } from "@/components/session/add-session";
import { ChatEntityFeatureFlagsProvider } from "@/components/shared/chat-feature-flags-context";
import { useHubStore } from "@/components/hub/use-hub-store";
import { useAbility } from "@/lib/casl/ability-context";
import { cn } from "@/lib/utils";
import { usePopupStore } from "@/lib/hooks/conversations/use-popup-store";
import { useMentorWorkflowsSelection } from "../use-selection";
import { useActionStore } from "../action";
import type {
  AttentionBannerKind,
  AttentionBannerLaunchTarget,
} from "./attention-banner";
import { AttentionComposer } from "./attention-composer";
import type {
  AttentionComposerBanner,
  AttentionComposerSuggestions,
} from "./attention-composer-model";
import { AttentionHeader } from "./attention-header";
import {
  MENTOR_LIVE_CAPABILITIES,
  type AttentionSurfaceCapabilities,
} from "./attention-capabilities";
import type { AttentionQuote } from "./attention-message";
import {
  AttentionActivityContent,
  AttentionEntityInteractionContent,
  AttentionTextBadge,
  AttentionEntitySharedContent,
} from "./attention-special-content";
import { AttentionSuggestions } from "./attention-suggestions";
import {
  AttentionThread,
  type AttentionSpecialContentRenderers,
} from "./attention-thread";
import type { UseAttentionAnalyticsResult } from "./use-attention-analytics";
import { useAttentionBanner } from "./use-attention-banner";
import type { UseAttentionBannerResult } from "./use-attention-banner";
import { useAttentionChannel } from "./use-attention-channel";
import { useAttentionComposer } from "./use-attention-composer";
import { useAttentionEmojis } from "./use-attention-emojis";
import { useAttentionHeader } from "./use-attention-header";

const ATTENTION_MESSAGE_LIST_QUERY = {
  includeParentMessageInfo: true,
  replyType: ReplyType.ALL,
};

export function AttentionWorkspace() {
  const { selectedConversation, segment } = useMentorWorkflowsSelection();
  const channelUrl = selectedConversation?.channel ?? "";
  // Support/internal-ops chats have no AI mentor suggestions.
  const supportsSuggestions = segment !== "support";

  // Mentor live is the only wired surface today; training/admin plug in via this seam.
  const capabilities: AttentionSurfaceCapabilities = MENTOR_LIVE_CAPABILITIES;

  const ability = useAbility();
  const canEscalate = ability.can("create", "escalations");
  const { setEscalationPopupOpen } = usePopupStore();

  const [callModalOpen, setCallModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const handleCallTeen = useCallback(() => setCallModalOpen(true), []);
  const handleScheduleCall = useCallback(() => setScheduleModalOpen(true), []);
  const handleEscalate = useCallback(
    () => setEscalationPopupOpen(true),
    [setEscalationPopupOpen],
  );
  // "Report" is still a disabled "coming soon" item in the header.
  const handleComingSoon = useCallback(() => {}, []);

  const { headerProps, menteeId, scheduleMenteeId, analytics } = useAttentionHeader({
    channelUrl,
    segment,
    capabilities,
    onCallTeen: handleCallTeen,
    onScheduleCall: handleScheduleCall,
    onEscalate: handleEscalate,
    onReport: handleComingSoon,
  });

  const launchAction = useActionStore((s) => s.launch);
  const expandAction = useActionStore((s) => s.expand);
  const openHubToView = useHubStore((s) => s.openToView);
  const handleBannerLaunch = useCallback(
    (_kind: AttentionBannerKind, target: AttentionBannerLaunchTarget) => {
      switch (target) {
        case "create_swap":
          expandAction();
          openHubToView("create-swap");
          return;
        case "create_moment":
          expandAction();
          openHubToView("create-moment");
          return;
        case "action_panel":
          launchAction();
          return;
        default: {
          const exhaustive: never = target;
          return exhaustive;
        }
      }
    },
    [expandAction, launchAction, openHubToView],
  );
  const banner = useAttentionBanner({
    menteeId,
    enabled: capabilities.canCompose,
    onLaunchAction: handleBannerLaunch,
    analytics,
  });

  if (!selectedConversation) {
    return null;
  }

  return (
    <section
      aria-label="Attention"
      className={cn(
        "relative flex h-full flex-1 flex-col",
        segment !== "support" && "min-w-[420px]",
      )}
    >
      <AttentionHeader {...headerProps} />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <GroupChannelProvider
          channelUrl={channelUrl}
          replyType="QUOTE_REPLY"
          messageListQueryParams={ATTENTION_MESSAGE_LIST_QUERY}
        >
          <ChatEntityFeatureFlagsProvider>
            <AttentionConversation
              channelUrl={channelUrl}
              capabilities={capabilities}
              supportsSuggestions={supportsSuggestions}
              banner={banner}
              analytics={analytics}
            />
          </ChatEntityFeatureFlagsProvider>
        </GroupChannelProvider>
      </div>

      {menteeId ? (
        <>
          <CallConfirmationModal
            otherUserId={menteeId}
            open={callModalOpen}
            onClose={() => setCallModalOpen(false)}
          />
          {scheduleMenteeId ? (
            <ScheduleSessionDialog
              menteeId={scheduleMenteeId}
              open={scheduleModalOpen}
              onOpenChange={setScheduleModalOpen}
            />
          ) : null}
        </>
      ) : null}

      {canEscalate && banner.mentorshipId ? (
        <Escalation mentorshipId={banner.mentorshipId} showFab={false} />
      ) : null}
    </section>
  );
}

// Composer and thread share one GroupChannelProvider; reply state is lifted so a
// thread tap populates the composer. Composer renders only when the surface can compose.
function AttentionConversation({
  channelUrl,
  capabilities,
  supportsSuggestions,
  banner,
  analytics,
}: {
  channelUrl: string;
  capabilities: AttentionSurfaceCapabilities;
  supportsSuggestions: boolean;
  banner: UseAttentionBannerResult;
  analytics: UseAttentionAnalyticsResult;
}) {
  const [quote, setQuote] = useState<AttentionQuote | null>(null);
  // Native Sendbird emoji set; the only place that touches Sendbird for emojis.
  const { reactionOptions, emojiUrlByKey } = useAttentionEmojis();
  const {
    view,
    renderContext,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
    resolveQuote,
    onToggleReaction,
  } = useAttentionChannel(emojiUrlByKey);

  const handleReply = useCallback(
    (messageId: string) => {
      const next = resolveQuote(messageId);
      if (next) setQuote(next);
    },
    [resolveQuote],
  );

  // Render context carries real participants so captions resolve first names.
  const specialContent = useMemo<AttentionSpecialContentRenderers>(
    () => ({
      activity: (content) => (
        <AttentionActivityContent source={content} context={renderContext} />
      ),
      entityShared: (content) => (
        <AttentionEntitySharedContent source={content} context={renderContext} />
      ),
      entityInteraction: (item) => (
        <AttentionEntityInteractionContent source={item} context={renderContext} />
      ),
      textBadge: (item) => <AttentionTextBadge rawData={item.rawData} />,
    }),
    [renderContext],
  );

  return (
    <>
      <AttentionThread
        view={view}
        capabilities={capabilities}
        // Falls back to the thread's DEFAULT_REACTIONS until Sendbird emojis load.
        reactionChoices={
          reactionOptions.length > 0 ? reactionOptions : undefined
        }
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        error={error}
        hasMore={hasMore}
        onLoadMore={loadMore}
        onReply={capabilities.canCompose ? handleReply : undefined}
        onToggleReaction={capabilities.canReact ? onToggleReaction : undefined}
        specialContent={specialContent}
      />
      {capabilities.canCompose ? (
        <AttentionComposerConnected
          channelUrl={channelUrl}
          quote={quote}
          onQuoteChange={setQuote}
          capabilities={capabilities}
          supportsSuggestions={supportsSuggestions}
          banner={banner}
          analytics={analytics}
        />
      ) : null}
    </>
  );
}

function AttentionComposerConnected({
  channelUrl,
  quote,
  onQuoteChange,
  capabilities,
  supportsSuggestions,
  banner,
  analytics,
}: {
  channelUrl: string;
  quote: AttentionQuote | null;
  onQuoteChange: (quote: AttentionQuote | null) => void;
  capabilities: AttentionSurfaceCapabilities;
  supportsSuggestions: boolean;
  banner: UseAttentionBannerResult;
  analytics: UseAttentionAnalyticsResult;
}) {
  const store = useSendbirdStateContext();
  const userId = store.config.userId;
  const vm = useAttentionComposer({
    channelUrl,
    quote,
    onQuoteChange,
    bannerActive: banner.banner !== null,
    analytics,
  });

  const { insertSuggestion } = vm;
  const handleInsertSuggestion = useCallback(
    (
      text: string,
      suggestionType: "default" | "specific" | "priority",
      approach: string,
    ) => {
      insertSuggestion(text);
      analytics.trackSuggestionInsert(suggestionType, approach);
    },
    [insertSuggestion, analytics],
  );

  const suggestions = useMemo<AttentionComposerSuggestions>(
    () => ({
      enabled: capabilities.canCompose && supportsSuggestions,
      slot: (
        <AttentionSuggestions
          mentorshipId={banner.mentorshipId}
          userId={userId}
          quotedText={quote?.text ?? null}
          onInsert={handleInsertSuggestion}
          onRegenerate={analytics.trackSuggestionRegenerate}
        />
      ),
    }),
    [
      capabilities.canCompose,
      supportsSuggestions,
      banner.mentorshipId,
      userId,
      quote?.text,
      handleInsertSuggestion,
      analytics.trackSuggestionRegenerate,
    ],
  );

  const bannerSlice = useMemo<AttentionComposerBanner>(
    () => ({
      descriptor: banner.banner,
      open: banner.open,
      onToggle: banner.onToggle,
      onAction: banner.onAction,
      onInsertMagicMoment: insertSuggestion,
    }),
    [
      banner.banner,
      banner.open,
      banner.onToggle,
      banner.onAction,
      insertSuggestion,
    ],
  );

  return (
    <AttentionComposer
      editor={vm.editor}
      quote={vm.quote}
      mode={vm.mode}
      image={vm.image}
      gif={vm.gif}
      voice={vm.voice}
      suggestions={suggestions}
      banner={bannerSlice}
    />
  );
}
